import {useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {
  WarningOutlined,
  HomeOutlined,
  PhoneOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import {
  getPayment,
  getTransactionsByIds,
  searchSubscriptionsByGranteeId,
  PaymentStatus,
  type SubscriptionV2Summary,
  type ScheduledInstallment,
  type Transaction,
  collectStampedTxnIds,
} from "../../api/payment";
import {getStudentById} from "../../api/students";
import {getCampusesByIds, type Campus} from "../../api/user";
import type {ShiftSchedule, SubscriptionShiftDetails} from "../../api/payment";
import {useAuth} from "../../context/AuthContext";
import {useToastContext} from "../../context/ToastContext";
import {ROUTES} from "@utils/constants";
import {formatDate} from "@utils/dateFormatter";
import {calculateEstimatedPickupTime, formatDuration} from "@utils/formatters";
import { PaymentSummaryCard } from "../../components/subscription/PaymentSummaryCard";
import { PaymentHistoryCard } from "../../components/subscription/PaymentHistoryCard";
import { RoutingDisclaimer } from "@components/ui/RoutingDisclaimer/RoutingDisclaimer";
import { computePaymentSummary, firstPendingInstallment } from "../../utils/subscriptionHelpers";
import "./styles/success.css";
import { GetStudentOutput } from "@dev-zeromoblt/o-platform-client";

const RETRY_COUNT = 3;
const RETRY_DELAY_MS = 3000; // 3 seconds between retries

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const txnid = searchParams.get("txnid");
  const {parentId} = useAuth();
  const { addToast } = useToastContext();

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] =
    useState<SubscriptionV2Summary | null>(null);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(
    null,
  );
  const [studentDetails, setStudentDetails] = useState<GetStudentOutput | null>(null);
  const [campusData, setCampusData] = useState<Campus | null>(null);
  const [justPaidPaymentId, setJustPaidPaymentId] = useState<string | undefined>();
  const [paymentDate, setPaymentDate] = useState<number | undefined>();
  const [paidAmountFromPayload, setPaidAmountFromPayload] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  const supportNumber = import.meta.env.VITE_PARENT_SUPPORT_NUMBER;

  const handleContactSupport = () => {
    if (!supportNumber) {
      addToast({
        type: 'error',
        message: 'Unable to contact support right now. Please try again later.',
      });
      return;
    }
    window.location.href = `tel:${supportNumber}`;
  };

  useEffect(() => {
    if (!txnid) {
      navigate(ROUTES.WELCOME);
      return;
    }

    const run = async () => {
      try {
        setLoading(true);

        // Step 1: Fetch payment details from API
        const paymentData = await getPayment(txnid);

        // Step 2: Check if payment status is SUCCESS
        if (!paymentData || paymentData.status !== PaymentStatus.SUCCESS) {
          throw new Error(
            `Payment status is ${paymentData.status}. Please contact support.`,
          );
        }

        setJustPaidPaymentId(paymentData.id ?? txnid);
        // Store transaction date from payment payload
        if (paymentData.updatedAt || paymentData.createdAt) {
          setPaymentDate(paymentData.updatedAt ?? paymentData.createdAt);
        }
        // Store paid amount directly from payment payload
        if (paymentData.amount) {
          const parsed = parseFloat(paymentData.amount);
          if (!isNaN(parsed)) setPaidAmountFromPayload(parsed);
        }

        // Step 3: Fetch student details to get startDate
        const studentData = await getStudentById(paymentData?.granteeId!!);
        if (studentData) {
          setStudentDetails(studentData);
          // Fetch campus and shift for trip details on success page
          if (studentData.campusId) {
            getCampusesByIds([studentData.campusId])
              .then(res => { if (res.campuses && res.campuses?.length > 0) setCampusData(res.campuses[0]); })
              .catch(() => {});
          }
        }

        // Step 4: Search for the subscription the backend created/updated after payment.
        // Wait 12 seconds for the SFN workflow to complete and DDB stream notification delay.
        await sleep(12000);

        const studentStartDate = studentData?.startDate;
        const justPaidId = paymentData.id ?? txnid;
        // Present for installment payments; absent for new subscription (token) payments.
        const paidInstallmentNumber = paymentData.subscriptionV2InstallmentNumber;

        // lastCandidateSub: the subscription matched by the search (even if payment not yet reflected).
        // foundSubscription: set only once the payment is confirmed attached on the subscription.
        let lastCandidateSub: SubscriptionV2Summary | null = null;
        let foundSubscription: SubscriptionV2Summary | null = null;

        for (let attempt = 1; attempt <= RETRY_COUNT; attempt++) {
          try {
            const searchResult = await searchSubscriptionsByGranteeId(studentData?.id!!);
            const subscriptions = searchResult.subscriptions || [];

            // Only search for ACTIVE subscriptions on success page.
            // PENDING_PAYMENT subscriptions belong in Dashboard/admin context, not payment success.
            const sortedPayable = [...subscriptions]
              .filter(
                (sub) =>
                  sub.status === "ACTIVE",
              )
              .sort(
                (a, b) =>
                  ((b.updatedAt ?? b.createdAt ?? 0) -
                  (a.updatedAt ?? a.createdAt ?? 0)),
              );

            // 1. Exact match: subscription whose token or installment carries this paymentId.
            //    Unambiguous — the backend stamps the paymentId on whichever sub was updated.
            const byPaymentId = sortedPayable.find((sub) => {
              if (sub.token?.paymentId === justPaidId) return true;
              return (sub.paymentCalendar?.installments ?? []).some(
                (inst: ScheduledInstallment) => inst.paymentId === justPaidId,
              );
            });

            // 2. Prefer PENDING_PAYMENT (replacement awaiting dues), then startDate match,
            //    then most recently updated.
            const byPendingPayment = sortedPayable.find(
              (sub) => sub.status === "PENDING_PAYMENT",
            );
            const byStartDate = sortedPayable.find(
              (sub) => sub.startDate === studentStartDate,
            );

            const candidate =
              byPaymentId ?? byPendingPayment ?? byStartDate ?? sortedPayable[0];

            if (candidate?.id) {
              lastCandidateSub = candidate;

              // Verify the payment is actually reflected in the subscription.
              let paymentAttached = false;
              if (paidInstallmentNumber) {
                // Installment payment: the specific installment must carry the paymentId and be marked paid.
                const targetInst = (candidate.paymentCalendar?.installments ?? []).find(
                  (inst: ScheduledInstallment) => inst.installmentNumber === paidInstallmentNumber,
                );
                paymentAttached = !!(
                  targetInst &&
                  targetInst.paymentId === justPaidId &&
                  (targetInst.status === "PAID" || targetInst.paidAt)
                );
              } else {
                // New subscription / token payment: paymentId must be on the token or any installment.
                paymentAttached =
                  candidate.token?.paymentId === justPaidId ||
                  (candidate.paymentCalendar?.installments ?? []).some(
                    (inst: ScheduledInstallment) => inst.paymentId === justPaidId,
                  );
              }

              if (paymentAttached) {
                foundSubscription = candidate;
                break;
              }
            }
          } catch (searchErr) {
            console.warn(`[PaymentSuccess] Search attempt ${attempt} failed:`, searchErr);
          }

          if (attempt < RETRY_COUNT) {
            await sleep(RETRY_DELAY_MS);
          }
        }

        // Step 5: Differentiate between "subscription never found" and "found but payment not yet reflected".
        if (!lastCandidateSub) {
          throw new Error(
            "Subscription not found after payment. The update may take a few minutes. Please contact support if it doesn't appear.",
          );
        }
        if (!foundSubscription) {
          throw new Error(
            "Payment received but the subscription update is still pending. This may take a few minutes. Please contact support if it doesn't reflect.",
          );
        }

        setSubscription(foundSubscription);
        localStorage.removeItem("pendingSubscription");

        // Extract stamped adjustment IDs (transactionIds[] or legacy transactionId)
        const txnIds = collectStampedTxnIds(
          foundSubscription.token,
          foundSubscription.paymentCalendar?.installments,
        );
        if (txnIds.length > 0) {
          const txns = await getTransactionsByIds(txnIds);
          setTransactions(txns);
        }
      } catch (err: unknown) {
        console.error("[PaymentSuccess] Error:", err);
        setSubscriptionError(err instanceof Error ? err.message : "Subscription activation failed");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [txnid, parentId, navigate]);

  if (loading) {
    return (
      <div className="payment-success payment-success--loading">
        <div className="payment-success__spinner"></div>
        <p>Activating your subscription...</p>
      </div>
    );
  }

  // Subscription creation FAILED
  if (subscriptionError) {
    return (
      <div className="payment-success">
        <div className="payment-success__container">
          <div className="payment-success__icon payment-success__icon--warning">
            <WarningOutlined style={{fontSize: 64, color: "#f59e0b"}} />
          </div>
          <h1 className="payment-success__title">Payment Received ✓</h1>
          <p className="payment-success__subtitle payment-success__subtitle--warning">
            Subscription activation is pending
          </p>
          <div className="payment-success__warning-card">
            <p>
              Your payment was successful, but we couldn't activate your
              subscription automatically.
            </p>
            <p style={{marginTop: 8}}>
              Our team will manually activate your subscription within{" "}
              <strong>2 hours</strong>. You'll receive confirmation via SMS.
            </p>
            <div className="payment-success__txn-ref">
              <span>Reference ID: </span>
              <strong>{txnid}</strong>
            </div>
          </div>
          <div className="payment-success__actions">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleContactSupport();
              }}
              className="payment-success__button payment-success__button--primary"
              rel="noreferrer">
              <PhoneOutlined /> Contact Support
            </a>
            <button
              onClick={() => navigate(ROUTES.WELCOME)}
              className="payment-success__button payment-success__button--secondary">
              <HomeOutlined /> Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // SUCCESS — show full subscription details
  const cal = subscription?.paymentCalendar;
  const paymentPlan = subscription?.paymentPlan;
  const token = subscription?.token;
  const allInstallments = cal?.installments || [];
  const pendingInstallments = allInstallments.filter(
    (inst: ScheduledInstallment) => inst.status !== "PAID" && !inst.paidAt,
  );

  // Identify what was just paid for the banner
  const justPaidToken = token?.paymentId === justPaidPaymentId ? token : null;
  const justPaidInstallment = allInstallments.find(
    (i: ScheduledInstallment) => i.paymentId === justPaidPaymentId,
  ) ?? null;

  // Format transaction date for banner
  const txnDateStr = paymentDate
    ? new Date(paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';

  const bannerSubtitle = justPaidToken
    ? `Token Payment${txnDateStr ? ` · ${txnDateStr}` : ''}`
    : justPaidInstallment
      ? `Installment ${justPaidInstallment.installmentNumber} of ${allInstallments.length}${txnDateStr ? ` · ${txnDateStr}` : ''}`
      : 'Payment confirmed';

  // Use amount from payment payload as the source of truth for the banner.
  // Fall back to subscription installment/token amount if payload amount isn't available.
  const bannerAmount = paidAmountFromPayload
    ?? (justPaidToken ? justPaidToken.amount : justPaidInstallment?.amount ?? null);

  // Show pending notice if the paid amount matches an installment that is still PENDING.
  // This happens when the SFN workflow hasn't completed yet (can take 5-10 mins).
  const pendingMatchedInstallment = paidAmountFromPayload != null
    ? allInstallments.find(
        (i: ScheduledInstallment) =>
          i.amount === Math.round(paidAmountFromPayload) &&
          (i.status === "PENDING" || i.status === "OVERDUE") &&
          !i.paidAt,
      )
    : null;

  return (
    <div className="payment-success">
      <div className="payment-success__container">
        {/* ── Green gradient banner ── */}
        <div className="payment-success__banner">
          <span className="payment-success__banner-icon">✅</span>
          <h1 className="payment-success__banner-title">Payment Successful!</h1>
          <p className="payment-success__banner-subtitle">{bannerSubtitle}</p>
          {bannerAmount != null && (
            <p className="payment-success__banner-amount">
              ₹{bannerAmount.toLocaleString('en-IN')}
            </p>
          )}
        </div>

        {/* Pending installment update notice */}
        {pendingMatchedInstallment && (
          <div className="payment-success__pending-notice">
            <ClockCircleOutlined className="payment-success__pending-notice-icon" />
            <div>
              <p className="payment-success__pending-notice-title">Update in progress</p>
              <p className="payment-success__pending-notice-body">
                Your installment payment was received. The subscription update may take{" "}
                <strong>5–10 minutes</strong> to reflect. If it hasn't updated after that,
                please{" "}
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); handleContactSupport(); }}
                  className="payment-success__pending-notice-link"
                >
                  contact support
                </a>
                .
              </p>
            </div>
          </div>
        )}

        {/* Card 3: Subscription Details */}
        {studentDetails && <div className="payment-success__card">
          <h3 className="payment-success__card-title">Subscription Details</h3>
          {/* Student name — first row */}
          {studentDetails.name && (
            <div className="payment-success__detail">
              <span className="payment-success__label">Student</span>
              <span className="payment-success__value">{studentDetails.name}</span>
            </div>
          )}
          {studentDetails?.startDate && <div className="payment-success__detail">
            <span className="payment-success__label">Subscription Starts</span>
            <span className="payment-success__value payment-success__value--mono">
              {formatDate(studentDetails.startDate)}
            </span>
          </div>}
          {(() => {
            // First payable installment (PENDING|OVERDUE) for token validity end date
            const nextDue = firstPendingInstallment(cal?.installments ?? []);
            
            // Only show token validity end date if there are pending installments
            if (nextDue?.dueDate) {
              return (
                <div className="payment-success__detail">
                  <span className="payment-success__label">Token Validity Ends</span>
                  <span className="payment-success__value payment-success__value--mono">{formatDate(nextDue.dueDate)}</span>
                </div>
              );
            }
            return null;
          })()}
          {subscription?.shiftDetails?.validUntil && (
            <div className="payment-success__detail">
              <span className="payment-success__label">Subscription Ends</span>
              <span className="payment-success__value payment-success__value--mono">
                {formatDate(subscription.shiftDetails.validUntil)}
              </span>
            </div>
          )}
          {subscription?.status && <div className="payment-success__detail">
            <span className="payment-success__label">Status</span>
            <span className="payment-success__badge payment-success__badge--active">
              {subscription?.status || "ACTIVE"}
            </span>
          </div>}
          <div className="payment-success__detail">
            <span className="payment-success__label">Plan</span>
            <span className="payment-success__value">
              {paymentPlan?.name || "Transport Plan"}
            </span>
          </div>
        </div>}

        {/* Card: Trip Details */}
        {studentDetails && (
          <div className="payment-success__card">
            <h3 className="payment-success__card-title">Trip Details</h3>
            {/* Distance Slab */}
            {(() => {
              const tier = subscription?.pricingDetails?.tier;
              return tier?.rangeKm ? (
                <div className="payment-success__detail">
                  <span className="payment-success__label">Distance Slab</span>
                  <span className="payment-success__value">{tier.rangeKm.from}–{tier.rangeKm.to} km</span>
                </div>
              ) : null;
            })()}
            {/* Trip Type */}
            {studentDetails.tags?.transportType && (
              <div className="payment-success__detail">
                <span className="payment-success__label">Trip Type</span>
                <span className="payment-success__value">
                  {studentDetails.tags.transportType === 'AC' ? '❄️ AC' : '🌬️ Non-AC'}
                </span>
              </div>
            )}
            {/* Pickup Location */}
            {studentDetails.location && (
              <div className="payment-success__detail">
                <span className="payment-success__label">Pickup Location</span>
                <span className="payment-success__value">
                  <a
                    href={`https://www.google.com/maps?q=${studentDetails.location.lat},${studentDetails.location.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#3b82f6' }}
                  >
                    📍 View on Maps
                  </a>
                </span>
              </div>
            )}
            {/* Drop Location */}
            {campusData && (
              <div className="payment-success__detail">
                <span className="payment-success__label">Drop Location</span>
                <span className="payment-success__value">{campusData.name}</span>
              </div>
            )}
            {/* Estimated Pickup Time */}
            {(subscription?.shiftDetails as SubscriptionShiftDetails & { schedule?: ShiftSchedule })?.schedule?.startsAt && studentDetails?.tags?.travelTime && (
              <div className="payment-success__detail">
                <span className="payment-success__label">Estimated Pickup Time</span>
                <span className="payment-success__value">
                  {calculateEstimatedPickupTime(
                    (subscription!.shiftDetails as SubscriptionShiftDetails & { schedule?: ShiftSchedule }).schedule!.startsAt,
                    Math.round(parseFloat(String(studentDetails.tags.travelTime)))
                  ) || '—'}
                </span>
              </div>
            )}
            {/* Estimated Distance */}
            {studentDetails.tags?.distanceToCampus && (
              <div className="payment-success__detail">
                <span className="payment-success__label">Estimated Distance</span>
                <span className="payment-success__value">{studentDetails.tags.distanceToCampus} km</span>
              </div>
            )}
            {/* Travel Time */}
            {studentDetails.tags?.travelTime && (
              <div className="payment-success__detail">
                <span className="payment-success__label">Travel Time</span>
                <span className="payment-success__value">
                  ~{formatDuration(parseFloat(String(studentDetails.tags.travelTime)))}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Payment Summary + History */}
        {cal && (() => {
          const summary = computePaymentSummary(
            token ?? null,
            cal.totalAmount ?? 0,
            allInstallments,
            transactions,
          );
          const pendingCount = pendingInstallments.length;
          return (
            <>
              <PaymentSummaryCard
                totalPaid={summary.totalPaid}
                totalSubscription={summary.totalSubscription}
                adjustedPaid={summary.adjustedPaid}
                adjustedTotal={summary.adjustedTotal}
                remaining={summary.remaining}
                progressPct={summary.progressPct}
                pendingInstallments={pendingCount}
              />
              <PaymentHistoryCard
                token={token && (token.amount ?? 0) > 0 ? token : undefined}
                installments={allInstallments}
                transactions={transactions}
                justPaidPaymentId={justPaidPaymentId}
              />
            </>
          );
        })()}

        {/* Routing availability disclaimer — only when not active */}
        {studentDetails?.status !== 'ACTIVE' && <RoutingDisclaimer variant="full" />}

        <button
          onClick={() => navigate(ROUTES.WELCOME)}
          className="payment-success__button payment-success__button--primary"
          style={{width: "100%", marginTop: 8}}>
          <HomeOutlined /> Go to Dashboard
        </button>
      </div>
    </div>
  );
}
